import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

// ========================================
// SIGNAL ORCHESTRATOR v2.2 ULTIMATE
// ========================================
// COMPLETE FEATURE SET - ALL V 2.1 + V 2.2 FEATURES:
//
// V 2.2 FEATURES:
// - Rule Status Dashboard with individual rule metrics
// - Live Alerts Tray for real-time signal monitoring
// - Rule health indicators
// - Performance metrics per rule
// - Expandable alert details
// - Auto-refresh stats every 10 seconds
//
// V 2.1 ENHANCED FEATURES (NOW RESTORED):
// - Dynamic template variables (pass ANY field from TradingView)
// - Gotify notifications (optional push notifications)
// - Grist logging (optional permanent database logs)
// - Worker config sync (Gotify/Grist settings sent to worker)
// - Comma input bug fix
// - Production rule deletion
//
// ALL BASE V 2.1 FEATURES:
// - Test mode (browser-based testing)
// - Production mode (24/7 worker automation)
// - Rule management and evaluation
// - Signal polling and history
// - Action logging
// ========================================

const POLL_INTERVAL = 5000; // Poll every 5 seconds

function App() {
  // ========================================
  // CONFIGURATION STATE
  // ========================================
  const [isConfigured, setIsConfigured] = useState(false);
  const [workerUrl, setWorkerUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [dashboardPassword, setDashboardPassword] = useState('');
  const [configError, setConfigError] = useState('');

  // Gotify configuration
  const [gotifyEnabled, setGotifyEnabled] = useState(false);
  const [gotifyUrl, setGotifyUrl] = useState('');
  const [gotifyToken, setGotifyToken] = useState('');

  // Grist configuration
  const [gristEnabled, setGristEnabled] = useState(false);
  const [gristDocId, setGristDocId] = useState('');
  const [gristApiKey, setGristApiKey] = useState('');
  const [gristIncomingTableId, setGristIncomingTableId] = useState('');
  const [gristDispatchedTableId, setGristDispatchedTableId] = useState('');

  // ========================================
  // AUTHENTICATION
  // ========================================
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // ========================================
  // MODE MANAGEMENT
  // ========================================
  const [operationMode, setOperationMode] = useState('test');
  const [workerMode, setWorkerMode] = useState(null);

  // ========================================
  // SIGNAL POLLING STATE
  // ========================================
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastPollTime, setLastPollTime] = useState(null);
  
  // ========================================
  // RULE MANAGEMENT
  // ========================================
  const [rules, setRules] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);

  // ========================================
  // MATCHED RULES & ACTIONS
  // ========================================
  const [matchedRules, setMatchedRules] = useState([]);
  const [actionLog, setActionLog] = useState([]);

  // ========================================
  // SERVER-SIDE RULES (PRODUCTION MODE)
  // ========================================
  const [serverRules, setServerRules] = useState([]);
  const [productionStatus, setProductionStatus] = useState(null);

  // ========================================
  // NEW: RULE METRICS & PERFORMANCE
  // ========================================
  const [ruleMetrics, setRuleMetrics] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);

  // ========================================
  // SIGNALS & HISTORY
  // ========================================
  const [signals, setSignals] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);

  // ========================================
  // UI STATE
  // ========================================
  const [activeTab, setActiveTab] = useState('signals');
  const [productionTab, setProductionTab] = useState('dashboard'); // NEW: dashboard, control, rules
  const [notification, setNotification] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ========================================
  // NEW: ALERTS TRAY STATE
  // ========================================
  const [alertsTrayOpen, setAlertsTrayOpen] = useState(true);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [alertFilter, setAlertFilter] = useState('all'); // all, long, short, close

  // ========================================
  // LOAD CONFIGURATION FROM LOCALSTORAGE
  // ========================================
  useEffect(() => {
    const savedWorkerUrl = localStorage.getItem('so_worker_url');
    const savedApiToken = localStorage.getItem('so_api_token');
    const savedPassword = localStorage.getItem('so_dashboard_password');
    const savedAuth = localStorage.getItem('so_authenticated');

    // Load Gotify config
    const savedGotifyEnabled = localStorage.getItem('so_gotify_enabled');
    const savedGotifyUrl = localStorage.getItem('so_gotify_url');
    const savedGotifyToken = localStorage.getItem('so_gotify_token');

    // Load Grist config
    const savedGristEnabled = localStorage.getItem('so_grist_enabled');
    const savedGristDocId = localStorage.getItem('so_grist_doc_id');
    const savedGristApiKey = localStorage.getItem('so_grist_api_key');
    const savedGristIncomingTableId = localStorage.getItem('so_grist_incoming_table_id');
    const savedGristDispatchedTableId = localStorage.getItem('so_grist_dispatched_table_id');

    if (savedGotifyEnabled === 'true') {
      setGotifyEnabled(true);
      setGotifyUrl(savedGotifyUrl || '');
      setGotifyToken(savedGotifyToken || '');
    }

    if (savedGristEnabled === 'true') {
      setGristEnabled(true);
      setGristDocId(savedGristDocId || '');
      setGristApiKey(savedGristApiKey || '');
      setGristIncomingTableId(savedGristIncomingTableId || '');
      setGristDispatchedTableId(savedGristDispatchedTableId || '');
    }

    if (savedWorkerUrl && savedApiToken && savedPassword) {
      setWorkerUrl(savedWorkerUrl);
      setApiToken(savedApiToken);
      setDashboardPassword(savedPassword);
      setIsConfigured(true);

      if (savedAuth === 'true') {
        setIsAuthenticated(true);
      }
    }

    // Load saved rules
    const savedRules = localStorage.getItem('so_rules');
    if (savedRules) {
      try {
        setRules(JSON.parse(savedRules));
      } catch (e) {
        console.error('Failed to load rules:', e);
      }
    }

    // Load saved signals
    const savedSignals = localStorage.getItem('so_signals');
    if (savedSignals) {
      try {
        setSignals(JSON.parse(savedSignals));
      } catch (e) {
        console.error('Failed to load signals:', e);
      }
    }

    // Load action log
    const savedActionLog = localStorage.getItem('so_action_log');
    if (savedActionLog) {
      try {
        setActionLog(JSON.parse(savedActionLog));
      } catch (e) {
        console.error('Failed to load action log:', e);
      }
    }

    // Load history
    const savedHistory = localStorage.getItem('so_history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Failed to load history:', e);
      }
    }

    // NEW: Load alerts tray state
    const savedAlertsTray = localStorage.getItem('so_alerts_tray_open');
    if (savedAlertsTray !== null) {
      setAlertsTrayOpen(savedAlertsTray === 'true');
    }
  }, []);

  // ========================================
  // SAVE CONFIGURATION TO LOCALSTORAGE
  // ========================================
  useEffect(() => {
    if (rules.length > 0) {
      localStorage.setItem('so_rules', JSON.stringify(rules));
    }
  }, [rules]);

  useEffect(() => {
    if (signals.length > 0) {
      localStorage.setItem('so_signals', JSON.stringify(signals));
    }
  }, [signals]);

  useEffect(() => {
    if (actionLog.length > 0) {
      localStorage.setItem('so_action_log', JSON.stringify(actionLog));
    }
  }, [actionLog]);

  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('so_history', JSON.stringify(history));
    }
  }, [history]);

  // NEW: Save alerts tray state
  useEffect(() => {
    localStorage.setItem('so_alerts_tray_open', alertsTrayOpen.toString());
  }, [alertsTrayOpen]);

  // ========================================
  // NEW: UPDATE RECENT ALERTS WHEN SIGNALS CHANGE
  // ========================================
  useEffect(() => {
    if (signals.length > 0) {
      const alerts = signals.slice(0, 20).map(signal => ({
        id: signal.id || Date.now(),
        timestamp: signal.timestamp,
        type: signal.strategy || 'Unknown',
        symbol: signal.symbol || 'N/A',
        action: signal.action || 'N/A',
        price: signal.close || signal.price || 'N/A',
        status: 'new',
        data: signal
      }));
      setRecentAlerts(alerts);
    }
  }, [signals]);

  // ========================================
  // NEW: CALCULATE RULE METRICS
  // ========================================
  useEffect(() => {
    if (serverRules.length > 0 && actionLog.length > 0) {
      const metrics = {};
      
      serverRules.forEach(rule => {
        const ruleActions = actionLog.filter(log => log.rule === rule.name);
        const successfulActions = ruleActions.filter(log => log.status === 'webhook_sent');
        const lastTriggered = ruleActions.length > 0 
          ? new Date(Math.max(...ruleActions.map(log => new Date(log.timestamp))))
          : null;
        
        metrics[rule.id] = {
          totalTriggers: ruleActions.length,
          successfulTriggers: successfulActions.length,
          failedTriggers: ruleActions.length - successfulActions.length,
          successRate: ruleActions.length > 0 
            ? Math.round((successfulActions.length / ruleActions.length) * 100) 
            : 0,
          lastTriggered,
          averageResponseTime: 0, // TODO: Track this in future
          status: rule.enabled ? 'active' : 'inactive'
        };
      });
      
      setRuleMetrics(metrics);
    }
  }, [serverRules, actionLog]);

  // ========================================
  // NOTIFICATION SYSTEM
  // ========================================
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);


  // ========================================
  // API HEADERS BUILDER (API TOKEN OPTIONAL)
  // ========================================
  const buildHeaders = useCallback((additionalHeaders = {}) => {
    const headers = { ...additionalHeaders };
    if (apiToken) {
      headers['X-API-Token'] = apiToken;
    }
    return headers;
  }, [apiToken]);

  // ========================================
  // CONFIGURATION FUNCTIONS
  // ========================================
  const saveConfiguration = () => {
    if (!workerUrl || !dashboardPassword) {
      setConfigError('Worker URL and Dashboard Password are required');
      return;
    }

    localStorage.setItem('so_worker_url', workerUrl);
    localStorage.setItem('so_api_token', apiToken);
    localStorage.setItem('so_dashboard_password', dashboardPassword);

    // Save Gotify config
    localStorage.setItem('so_gotify_enabled', gotifyEnabled.toString());
    if (gotifyEnabled) {
      localStorage.setItem('so_gotify_url', gotifyUrl);
      localStorage.setItem('so_gotify_token', gotifyToken);
    }

    // Save Grist config
    localStorage.setItem('so_grist_enabled', gristEnabled.toString());
    if (gristEnabled) {
      localStorage.setItem('so_grist_doc_id', gristDocId);
      localStorage.setItem('so_grist_api_key', gristApiKey);
      localStorage.setItem('so_grist_incoming_table_id', gristIncomingTableId);
      localStorage.setItem('so_grist_dispatched_table_id', gristDispatchedTableId);
    }

    setIsConfigured(true);
    setConfigError('');
    showNotification('Configuration saved successfully', 'success');
    
    // CRITICAL: Sync Gotify/Grist config to Worker
    syncWorkerConfig();
  };

  // ========================================
  // SYNC WORKER CONFIG (Gotify + Grist)
  // ========================================
  // This function sends Gotify and Grist configuration to the Worker
  // so it can send notifications and log webhooks automatically.
  // Called after saveConfiguration and on app load if already configured.
  const syncWorkerConfig = async () => {
    if (!workerUrl) return;
    
    try {
      await fetch(`${workerUrl}/api/config`, {
        method: 'POST',
        headers: {
...buildHeaders({ 'Content-Type': 'application/json' })
        },
        body: JSON.stringify({
          gotify: gotifyEnabled ? {
            url: gotifyUrl,
            token: gotifyToken
          } : null,
          grist: gristEnabled ? {
            docId: gristDocId,
            apiKey: gristApiKey,
            incomingTableId: gristIncomingTableId,
            dispatchedTableId: gristDispatchedTableId
          } : null
        })
      });
      console.log('âœ“ Worker config synced (Gotify/Grist)');
    } catch (error) {
      console.error('âœ— Error syncing Worker config:', error);
      // Don't throw - this shouldn't break the dashboard
    }
  };

  // ========================================
  // SYNC CONFIG ON FIRST LOAD
  // ========================================
  // If the user already has Gotify/Grist configured, sync it to the Worker
  // when the dashboard loads so the Worker has the latest configuration.
  useEffect(() => {
    if (isConfigured && workerUrl && (gotifyEnabled || gristEnabled)) {
      syncWorkerConfig();
    }
  }, [isConfigured]); // Only run when configuration status changes


  // ========================================
  // AUTHENTICATION FUNCTIONS
  // ========================================
  const handleLogin = () => {
    if (password === dashboardPassword) {
      setIsAuthenticated(true);
      localStorage.setItem('so_authenticated', 'true');
      setAuthError('');
    } else {
      setAuthError('Invalid password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('so_authenticated');
    setPassword('');
  };

  // ========================================
  // SIGNAL POLLING
  // ========================================
  useEffect(() => {
    if (!isMonitoring || !workerUrl || operationMode !== 'test') return;

    const pollSignals = async () => {
      try {
        const response = await fetch(`${workerUrl}/api/signals?since=${lastPollTime}`, {
          headers: buildHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          if (data.signals && data.signals.length > 0) {
            setSignals(prev => {
              const combined = [...data.signals, ...prev];
              const unique = Array.from(new Map(combined.map(s => [s.timestamp, s])).values());
              return unique.slice(0, 100);
            });

            setLastPollTime(new Date().toISOString());
            
            // Auto-evaluate rules when new signals arrive
            evaluateRules([...data.signals, ...signals]);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    const interval = setInterval(pollSignals, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isMonitoring, workerUrl, lastPollTime, apiToken, signals, operationMode]);

  // ========================================
  // RULE EVALUATION
  // ========================================
  const evaluateRules = useCallback((currentSignals) => {
    const now = Date.now();
    const activeRules = rules.filter(r => r.enabled);
    const newMatches = [];
    const newActions = [];

    activeRules.forEach(rule => {
      const recentSignals = currentSignals.filter(s => {
        const signalTime = new Date(s.timestamp).getTime();
        return now - signalTime <= (rule.timeWindow * 1000);
      });

      const matchingSignals = recentSignals.filter(s =>
        rule.requiredSignals.includes(s.strategy)
      );

      const groupedBySymbol = matchingSignals.reduce((acc, signal) => {
        if (!acc[signal.symbol]) acc[signal.symbol] = [];
        acc[signal.symbol].push(signal);
        return acc;
      }, {});

      Object.entries(groupedBySymbol).forEach(([symbol, symbolSignals]) => {
        if (symbolSignals.length >= rule.minSignals) {
          const matchId = `${rule.id}-${symbol}-${now}`;
          
          newMatches.push({
            id: matchId,
            ruleId: rule.id,
            ruleName: rule.name,
            symbol,
            signals: symbolSignals,
            timestamp: new Date().toISOString()
          });

          // Execute webhook if configured
          if (rule.webhook?.enabled) {
            executeWebhook(rule, symbolSignals[0], symbolSignals).then(success => {
              const action = {
                id: matchId,
                rule: rule.name,
                symbol,
                action: rule.action,
                signals: symbolSignals.map(s => s.strategy).join(', '),
                timestamp: new Date().toISOString(),
                status: success ? 'webhook_sent' : 'webhook_failed'
              };
              
              setActionLog(prev => [action, ...prev].slice(0, 100));
              
              if (success) {
                showNotification(`Rule "${rule.name}" triggered for ${symbol}`, 'success');
              }
            });
          } else {
            const action = {
              id: matchId,
              rule: rule.name,
              symbol,
              action: rule.action,
              signals: symbolSignals.map(s => s.strategy).join(', '),
              timestamp: new Date().toISOString(),
              status: 'no_webhook'
            };
            
            setActionLog(prev => [action, ...prev].slice(0, 100));
          }
        }
      });
    });

    if (newMatches.length > 0) {
      setMatchedRules(prev => [...newMatches, ...prev].slice(0, 50));
      setHistory(prev => [...newMatches.map(m => ({
        id: m.id,
        type: 'rule_matched',
        message: `Rule "${m.ruleName}" matched for ${m.symbol}`,
        timestamp: m.timestamp
      })), ...prev].slice(0, 100));
    }
  }, [rules, showNotification]);

  // ========================================
  // WEBHOOK EXECUTION
  // ========================================
  const executeWebhook = async (rule, primarySignal, allSignals) => {
    if (!rule.webhook?.enabled || !rule.webhook?.url) return false;

    try {
      let body = rule.webhook.body || '';
      
      // Replace template variables
      const replaceVariables = (text, signal) => {
        return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
          return signal[key] !== undefined ? signal[key] : match;
        });
      };

      body = replaceVariables(body, primarySignal);

      const response = await fetch(rule.webhook.url, {
        method: rule.webhook.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(rule.webhook.headers || {})
        },
        body: body || JSON.stringify({
          rule: rule.name,
          action: rule.action,
          signal: primarySignal,
          allSignals: allSignals
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Webhook error:', error);
      return false;
    }
  };

  // ========================================
  // SIMULATE SIGNAL (TEST MODE)
  // ========================================
  const simulateSignal = (strategy, action) => {
    const signal = {
      timestamp: new Date().toISOString(),
      strategy,
      symbol: 'BTCUSDT',
      action,
      close: '45000',
      open: '44800',
      high: '45200',
      low: '44700',
      volume: '1000'
    };

    setSignals(prev => [signal, ...prev].slice(0, 100));
    showNotification(`Simulated ${strategy} signal`, 'info');
  };

  // ========================================
  // PRODUCTION MODE FUNCTIONS
  // ========================================
  const fetchServerRules = async () => {
    if (!workerUrl) return;

    try {
      const response = await fetch(`${workerUrl}/api/rules`, {
        headers: buildHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setServerRules(data.rules || []);
      }
    } catch (error) {
      console.error('Failed to fetch server rules:', error);
    }
  };

  const fetchProductionStatus = async () => {
    if (!workerUrl) return;

    try {
      const response = await fetch(`${workerUrl}/api/status`, {
        headers: buildHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setProductionStatus(data);
        setWorkerMode(data.mode);
      }
    } catch (error) {
      console.error('Failed to fetch production status:', error);
    }
  };

  const deployRulesToProduction = async () => {
    if (!workerUrl) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${workerUrl}/api/rules`, {
        method: 'POST',
        headers: {
...buildHeaders({ 'Content-Type': 'application/json' })
        },
        body: JSON.stringify({ rules: rules.filter(r => r.enabled) })
      });

      if (response.ok) {
        await fetchServerRules();
        showNotification('Rules deployed successfully', 'success');
      } else {
        showNotification('Failed to deploy rules', 'error');
      }
    } catch (error) {
      console.error('Deployment error:', error);
      showNotification('Deployment error', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const setWorkerModeAPI = async (mode) => {
    if (!workerUrl) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${workerUrl}/api/mode`, {
        method: 'POST',
        headers: {
...buildHeaders({ 'Content-Type': 'application/json' })
        },
        body: JSON.stringify({ mode })
      });

      if (response.ok) {
        setWorkerMode(mode);
        await fetchProductionStatus();
        showNotification(`Worker mode set to ${mode.toUpperCase()}`, 'success');
      } else {
        showNotification('Failed to change worker mode', 'error');
      }
    } catch (error) {
      console.error('Mode change error:', error);
      showNotification('Mode change error', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteServerRule = async (ruleId) => {
    if (!workerUrl) return;

    if (!window.confirm('Delete this rule from production server?')) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${workerUrl}/api/rules/${ruleId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });

      if (response.ok) {
        await fetchServerRules();
        showNotification('Rule deleted from server', 'success');
      } else {
        showNotification('Failed to delete rule', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Delete error', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // RULE MANAGEMENT FUNCTIONS
  // ========================================
  const createNewRule = () => {
    const newRule = {
      id: `rule_${Date.now()}`,
      name: 'New Rule',
      requiredSignals: ['RSI_OVERSOLD'],
      minSignals: 1,
      timeWindow: 300,
      action: 'long',
      enabled: true,
      webhook: {
        enabled: false,
        url: '',
        method: 'POST',
        headers: {},
        body: ''
      }
    };
    setEditingRule(newRule);
    setShowRuleForm(true);
  };

  const saveRule = (rule) => {
    setRules(prev => {
      const existing = prev.find(r => r.id === rule.id);
      if (existing) {
        return prev.map(r => r.id === rule.id ? rule : r);
      }
      return [...prev, rule];
    });
    setShowRuleForm(false);
    setEditingRule(null);
    showNotification('Rule saved', 'success');
  };

  const toggleRule = (ruleId) => {
    setRules(prev => prev.map(r =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    ));
  };

  const deleteRule = (ruleId) => {
    if (window.confirm('Delete this rule?')) {
      setRules(prev => prev.filter(r => r.id !== ruleId));
      showNotification('Rule deleted', 'success');
    }
  };

  // ========================================
  // NEW: ALERTS TRAY FUNCTIONS
  // ========================================
  const getFilteredAlerts = () => {
    if (alertFilter === 'all') return recentAlerts;
    return recentAlerts.filter(alert => 
      alert.action.toLowerCase() === alertFilter.toLowerCase()
    );
  };

  const clearAlerts = () => {
    if (window.confirm('Clear all alerts?')) {
      setRecentAlerts([]);
      showNotification('Alerts cleared', 'success');
    }
  };

  // ========================================
  // PRODUCTION MODE: REFRESH STATUS
  // ========================================
  useEffect(() => {
    if (operationMode === 'production') {
      fetchServerRules();
      fetchProductionStatus();
      
      const interval = setInterval(() => {
        fetchProductionStatus();
      }, 10000); // Refresh every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [operationMode]);

  // ========================================
  // RENDER: CONFIGURATION SCREEN
  // ========================================
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-lg p-8 max-w-2xl w-full">
          <h1 className="text-2xl font-bold mb-6">Configure Signal Orchestrator</h1>
          
          {configError && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 mb-4">
              {configError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Worker URL</label>
              <input
                type="text"
                value={workerUrl}
                onChange={(e) => setWorkerUrl(e.target.value)}
                placeholder="https://your-worker.workers.dev"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">API Token (Optional)</label>
              <input
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder="Your API token"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Dashboard Password</label>
              <input
                type="password"
                value={dashboardPassword}
                onChange={(e) => setDashboardPassword(e.target.value)}
                placeholder="Set a password for this dashboard"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
              />
            </div>

            <button
              onClick={saveConfiguration}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: LOGIN SCREEN
  // ========================================
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold mb-6">Login to Dashboard</h1>
          
          {authError && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 mb-4">
              {authError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Enter your password"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
              />
            </div>

            <button
              onClick={handleLogin}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: MAIN DASHBOARD
  // ========================================
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Signal Orchestrator v2.2 ULTIMATE</h1>
            <p className="text-sm text-gray-400">
              {isMonitoring && operationMode === 'test' && (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  Polling every 5s {gotifyEnabled && '• Gotify'} {gristEnabled && '• Grist'}
                </span>
              )}
              {operationMode === 'production' && (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  Production Mode • Worker: {workerMode?.toUpperCase()}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* NEW: Alerts Tray Toggle */}
            <button
              onClick={() => setAlertsTrayOpen(!alertsTrayOpen)}
              className={`px-3 py-2 rounded-lg transition-colors relative ${
                alertsTrayOpen ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title="Toggle Alerts Tray"
            >
              🔔
              {recentAlerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {recentAlerts.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setShowSettings(true)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              ⚙️ Settings
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="flex gap-2">
          <button
            onClick={() => setOperationMode('test')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              operationMode === 'test'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
            }`}
          >
            🧪 Test Mode
          </button>
          <button
            onClick={() => {
              setOperationMode('production');
              fetchServerRules();
              fetchProductionStatus();
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              operationMode === 'production'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
            }`}
          >
            🚀 Production Mode
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed top-20 right-6 z-50 px-6 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-600' :
          notification.type === 'error' ? 'bg-red-600' :
          notification.type === 'warning' ? 'bg-yellow-600' :
          'bg-blue-600'
        }`}>
          {notification.message}
        </div>
      )}

      {/* NEW: ALERTS TRAY */}
      {alertsTrayOpen && (
        <div className="fixed right-0 top-32 bottom-0 w-96 bg-gray-800 border-l border-gray-700 shadow-2xl z-40 overflow-hidden flex flex-col">
          {/* Tray Header */}
          <div className="bg-gray-900 px-4 py-3 border-b border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">🔔 Live Alerts</h3>
              <button
                onClick={() => setAlertsTrayOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            {/* Filter Buttons */}
            <div className="flex gap-2">
              {['all', 'long', 'short', 'close'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setAlertFilter(filter)}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    alertFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Alerts List */}
          <div className="flex-1 overflow-y-auto">
            {getFilteredAlerts().length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-4xl mb-2">📭</div>
                <div>No alerts yet</div>
              </div>
            ) : (
              <div className="p-3 space-y-2">
                {getFilteredAlerts().map(alert => (
                  <div
                    key={alert.id}
                    className={`bg-gray-700 rounded-lg p-3 cursor-pointer transition-all hover:bg-gray-600 ${
                      expandedAlert === alert.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            alert.action === 'long' ? 'bg-green-900 text-green-300' :
                            alert.action === 'short' ? 'bg-red-900 text-red-300' :
                            'bg-yellow-900 text-yellow-300'
                          }`}>
                            {alert.action.toUpperCase()}
                          </span>
                          <span className="font-medium text-sm">{alert.symbol}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {alert.type} • ${alert.price}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(alert.timestamp).toLocaleTimeString()}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedAlert === alert.id && (
                      <div className="mt-3 pt-3 border-t border-gray-600 space-y-1 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(alert.data).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-400">{key}:</span>
                              <span className="ml-1 text-gray-200">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tray Footer */}
          {getFilteredAlerts().length > 0 && (
            <div className="bg-gray-900 px-4 py-3 border-t border-gray-700">
              <button
                onClick={clearAlerts}
                className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
              >
                Clear All Alerts
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${alertsTrayOpen ? 'mr-96' : ''}`}>
        {/* TEST MODE */}
        {operationMode === 'test' && (
          <div className="p-6">
            {/* Controls */}
            <div className="mb-6 flex gap-3 flex-wrap">
              <button
                onClick={() => {
                  setIsMonitoring(!isMonitoring);
                  if (!isMonitoring) {
                    setLastPollTime(new Date().toISOString());
                  }
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isMonitoring
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
              </button>

              <button
                onClick={() => evaluateRules(signals)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
              >
                Evaluate Rules Now
              </button>

              <button
                onClick={createNewRule}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
              >
                + New Rule
              </button>

              {rules.filter(r => r.enabled).length > 0 && (
                <button
                  onClick={deployRulesToProduction}
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors disabled:bg-gray-600"
                >
                  Deploy {rules.filter(r => r.enabled).length} Rules to Production
                </button>
              )}

              <button
                onClick={() => {
                  setSignals([]);
                  setHistory([]);
                  setActionLog([]);
                  setMatchedRules([]);
                  localStorage.removeItem('so_signals');
                  localStorage.removeItem('so_history');
                  localStorage.removeItem('so_action_log');
                  showNotification('Cleared all data', 'success');
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Clear All
              </button>
            </div>

            {/* Tabs */}
            <div className="bg-gray-800 rounded-lg">
              <div className="border-b border-gray-700 px-4 flex gap-4">
                {['signals', 'rules', 'history'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    {tab === 'signals' && `📡 Signals (${signals.length})`}
                    {tab === 'rules' && `⚙️ Rules (${rules.length})`}
                    {tab === 'history' && `📊 History (${actionLog.length})`}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Signals Tab */}
                {activeTab === 'signals' && (
                  <div>
                    <div className="mb-4 flex gap-2 flex-wrap">
                      <button
                        onClick={() => simulateSignal('RSI_OVERSOLD', 'long')}
                        className="px-3 py-2 bg-green-700 hover:bg-green-600 rounded-lg text-sm"
                      >
                        RSI Oversold (Long)
                      </button>
                      <button
                        onClick={() => simulateSignal('MACD_CROSS', 'long')}
                        className="px-3 py-2 bg-green-700 hover:bg-green-600 rounded-lg text-sm"
                      >
                        MACD Cross (Long)
                      </button>
                      <button
                        onClick={() => simulateSignal('VOLUME_SPIKE', 'long')}
                        className="px-3 py-2 bg-green-700 hover:bg-green-600 rounded-lg text-sm"
                      >
                        Volume Spike
                      </button>
                      <button
                        onClick={() => simulateSignal('RSI_OVERBOUGHT', 'short')}
                        className="px-3 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm"
                      >
                        RSI Overbought (Short)
                      </button>
                    </div>

                    <div className="space-y-2">
                      {signals.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          No signals yet. Start monitoring or simulate signals.
                        </div>
                      ) : (
                        signals.map(signal => (
                          <div key={signal.timestamp} className="bg-gray-700 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-semibold">{signal.strategy}</div>
                                <div className="text-sm text-gray-400">
                                  {signal.symbol} • {signal.action} • ${signal.close}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(signal.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Rules Tab */}
                {activeTab === 'rules' && (
                  <div>
                    <div className="space-y-3">
                      {rules.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          No rules configured. Create your first rule to get started.
                        </div>
                      ) : (
                        rules.map(rule => (
                          <div key={rule.id} className="bg-gray-700 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold">{rule.name}</h4>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    rule.enabled ? 'bg-green-900 text-green-300' : 'bg-gray-600 text-gray-400'
                                  }`}>
                                    {rule.enabled ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-400 mb-2">
                                  {rule.minSignals} of {rule.requiredSignals?.length || 0} signals • {rule.timeWindow}s window • {rule.action}
                                </p>
                                <div className="text-xs text-gray-500">
                                  Signals: {rule.requiredSignals?.join(', ')}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleRule(rule.id)}
                                  className={`px-3 py-1 rounded text-sm transition-colors ${
                                    rule.enabled
                                      ? 'bg-gray-600 hover:bg-gray-500'
                                      : 'bg-green-700 hover:bg-green-600'
                                  }`}
                                >
                                  {rule.enabled ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingRule(rule);
                                    setShowRuleForm(true);
                                  }}
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteRule(rule.id)}
                                  className="px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-sm"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Action History</h3>
                    <div className="space-y-2">
                      {actionLog.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          No actions yet. Rules will appear here when triggered.
                        </div>
                      ) : (
                        actionLog.map(log => (
                          <div key={log.id} className="bg-gray-700 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-semibold">{log.rule}</div>
                                <div className="text-sm text-gray-400">
                                  {log.symbol} • {log.action} • {log.signals}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className={`text-sm font-medium ${
                                  log.status === 'webhook_sent' ? 'text-green-400' :
                                  log.status === 'webhook_failed' ? 'text-red-400' :
                                  'text-gray-400'
                                }`}>
                                  {log.status === 'webhook_sent' && '✓ Sent'}
                                  {log.status === 'webhook_failed' && '✗ Failed'}
                                  {log.status === 'no_webhook' && '- No webhook'}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {new Date(log.timestamp).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PRODUCTION MODE */}
        {operationMode === 'production' && (
          <div className="p-6">
            {/* Production Tabs */}
            <div className="mb-6 flex gap-2">
              <button
                onClick={() => setProductionTab('dashboard')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  productionTab === 'dashboard'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                📊 Dashboard
              </button>
              <button
                onClick={() => setProductionTab('control')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  productionTab === 'control'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                🎛️ Control
              </button>
              <button
                onClick={() => setProductionTab('rules')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  productionTab === 'rules'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                ⚙️ Server Rules
              </button>
            </div>

            {/* Dashboard Tab - NEW */}
            {productionTab === 'dashboard' && (
              <div className="space-y-6">
                {/* Overview Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Total Signals</div>
                    <div className="text-3xl font-bold">{productionStatus?.totalSignals || 0}</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Rules Triggered</div>
                    <div className="text-3xl font-bold text-blue-400">{productionStatus?.rulesTriggered || 0}</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Actions Executed</div>
                    <div className="text-3xl font-bold text-green-400">{productionStatus?.actionsExecuted || 0}</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Success Rate</div>
                    <div className="text-3xl font-bold text-green-400">
                      {productionStatus?.actionsExecuted > 0
                        ? Math.round((productionStatus.successfulActions / productionStatus.actionsExecuted) * 100)
                        : 0}%
                    </div>
                  </div>
                </div>

                {/* Rule Status Cards */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">📈 Rule Performance</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {serverRules.length === 0 ? (
                      <div className="col-span-2 bg-gray-800 rounded-lg p-8 text-center text-gray-400">
                        No rules deployed to production yet
                      </div>
                    ) : (
                      serverRules.map(rule => {
                        const metrics = ruleMetrics[rule.id] || {};
                        const healthColor = 
                          metrics.successRate >= 90 ? 'text-green-400' :
                          metrics.successRate >= 70 ? 'text-yellow-400' :
                          'text-red-400';

                        return (
                          <div key={rule.id} className="bg-gray-800 rounded-lg p-5">
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h4 className="font-semibold text-lg mb-1">{rule.name}</h4>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    rule.enabled ? 'bg-green-900 text-green-300' : 'bg-gray-600 text-gray-400'
                                  }`}>
                                    {rule.enabled ? '🟢 Active' : '⚫ Inactive'}
                                  </span>
                                  {metrics.totalTriggers > 0 && (
                                    <span className={`font-semibold ${healthColor}`}>
                                      {metrics.successRate}% Success
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div>
                                <div className="text-gray-400 text-xs">Total Triggers</div>
                                <div className="text-xl font-bold">{metrics.totalTriggers || 0}</div>
                              </div>
                              <div>
                                <div className="text-gray-400 text-xs">Successful</div>
                                <div className="text-xl font-bold text-green-400">{metrics.successfulTriggers || 0}</div>
                              </div>
                            </div>

                            {metrics.lastTriggered && (
                              <div className="text-xs text-gray-400 border-t border-gray-700 pt-2">
                                Last triggered: {new Date(metrics.lastTriggered).toLocaleString()}
                              </div>
                            )}

                            <div className="text-xs text-gray-500 mt-2">
                              {rule.minSignals} of {rule.requiredSignals?.length || 0} signals • {rule.timeWindow}s window
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Control Tab */}
            {productionTab === 'control' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Production Status */}
                <div className="bg-gray-800 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">📊 Production Status</h3>
                  {productionStatus ? (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Mode:</span>
                        <span className={`font-semibold ${
                          productionStatus.mode === 'production' ? 'text-green-400' : 'text-yellow-400'
                        }`}>
                          {productionStatus.mode?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Signals:</span>
                        <span className="font-semibold">{productionStatus.totalSignals}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Rules Triggered:</span>
                        <span className="font-semibold">{productionStatus.rulesTriggered}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Actions Executed:</span>
                        <span className="font-semibold">{productionStatus.actionsExecuted}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Success Rate:</span>
                        <span className="font-semibold text-green-400">
                          {productionStatus.actionsExecuted > 0
                            ? Math.round((productionStatus.successfulActions / productionStatus.actionsExecuted) * 100)
                            : 0}%
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      Loading production status...
                    </div>
                  )}
                </div>

                {/* Worker Control */}
                <div className="bg-gray-800 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">🎛️ Worker Control</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Current Mode:</span>
                      <span className={`px-3 py-1 rounded font-semibold ${
                        workerMode === 'production'
                          ? 'bg-green-900 text-green-400'
                          : 'bg-yellow-900 text-yellow-400'
                      }`}>
                        {workerMode?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </div>
                    <button
                      onClick={() => setWorkerModeAPI('test')}
                      disabled={workerMode === 'test' || isLoading}
                      className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg font-medium disabled:bg-gray-600 transition-colors"
                    >
                      Switch to TEST
                    </button>
                    <button
                      onClick={() => setWorkerModeAPI('production')}
                      disabled={workerMode === 'production' || isLoading}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium disabled:bg-gray-600 transition-colors"
                    >
                      Switch to PRODUCTION
                    </button>
                    <button
                      onClick={() => {
                        fetchServerRules();
                        fetchProductionStatus();
                        showNotification('Status refreshed', 'success');
                      }}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                    >
                      🔄 Refresh Status
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Server Rules Tab */}
            {productionTab === 'rules' && (
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">⚙️ Server Rules</h3>
                <div className="space-y-3">
                  {serverRules.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      No rules deployed to production yet.
                    </div>
                  ) : (
                    serverRules.map(rule => (
                      <div key={rule.id} className="bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold">{rule.name}</h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {rule.minSignals} of {rule.requiredSignals?.length || 0} signals • {rule.timeWindow}s window
                            </p>
                            <div className="text-xs text-gray-500 mt-2">
                              Signals: {rule.requiredSignals?.join(', ')}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              rule.enabled
                                ? 'bg-green-900 text-green-400'
                                : 'bg-gray-600 text-gray-400'
                            }`}>
                              {rule.enabled ? 'Active' : 'Inactive'}
                            </span>
                            <button
                              onClick={() => deleteServerRule(rule.id)}
                              disabled={isLoading}
                              className="px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-sm disabled:bg-gray-600 transition-colors"
                              title="Delete from production server"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rule Edit Modal */}
      {showRuleForm && editingRule && (
        <RuleEditModal
          rule={editingRule}
          onSave={saveRule}
          onCancel={() => {
            setShowRuleForm(false);
            setEditingRule(null);
          }}
          onChange={setEditingRule}
          gotifyEnabled={gotifyEnabled}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 flex justify-between items-center border-b border-gray-700 sticky top-0 bg-gray-800">
              <h2 className="text-2xl font-bold">Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-300 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {configError && (
                <div className="bg-red-900/50 border border-red-700 rounded-lg p-3">
                  {configError}
                </div>
              )}

              {/* Core Configuration */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Core Settings</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Worker URL</label>
                  <input
                    type="text"
                    value={workerUrl}
                    onChange={(e) => setWorkerUrl(e.target.value)}
                    placeholder="https://your-worker.workers.dev"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">API Token (Optional)</label>
                  <input
                    type="password"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    placeholder="Your API token"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Dashboard Password</label>
                  <input
                    type="password"
                    value={dashboardPassword}
                    onChange={(e) => setDashboardPassword(e.target.value)}
                    placeholder="Set a password"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                  />
                </div>
              </div>

              {/* Gotify Configuration */}
              <div className="space-y-4 border-t border-gray-700 pt-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">📱 Gotify Notifications (Optional)</h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={gotifyEnabled}
                      onChange={(e) => setGotifyEnabled(e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-sm">Enable</span>
                  </label>
                </div>
                <p className="text-sm text-gray-400">Get push notifications when rules trigger</p>

                {gotifyEnabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">Gotify Server URL</label>
                      <input
                        type="text"
                        value={gotifyUrl}
                        onChange={(e) => setGotifyUrl(e.target.value)}
                        placeholder="https://gotify.example.com"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Gotify App Token</label>
                      <input
                        type="password"
                        value={gotifyToken}
                        onChange={(e) => setGotifyToken(e.target.value)}
                        placeholder="Your Gotify app token"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Grist Configuration */}
              <div className="space-y-4 border-t border-gray-700 pt-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">📊 Grist Logging (Optional)</h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={gristEnabled}
                      onChange={(e) => setGristEnabled(e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-sm">Enable</span>
                  </label>
                </div>
                <p className="text-sm text-gray-400">Log all webhooks to Grist database for permanent records</p>

                {gristEnabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">Grist Document ID</label>
                      <input
                        type="text"
                        value={gristDocId}
                        onChange={(e) => setGristDocId(e.target.value)}
                        placeholder="Your Grist document ID"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">Find this in your Grist document URL</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Grist API Key</label>
                      <input
                        type="password"
                        value={gristApiKey}
                        onChange={(e) => setGristApiKey(e.target.value)}
                        placeholder="Your Grist API key"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">Create in Grist Profile Settings â†’ API</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Incoming Webhooks Table ID</label>
                      <input
                        type="text"
                        value={gristIncomingTableId}
                        onChange={(e) => setGristIncomingTableId(e.target.value)}
                        placeholder="Incoming_Webhooks"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">Table for logging incoming TradingView webhooks</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Dispatched Webhooks Table ID</label>
                      <input
                        type="text"
                        value={gristDispatchedTableId}
                        onChange={(e) => setGristDispatchedTableId(e.target.value)}
                        placeholder="Dispatched_Webhooks"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">Table for logging outgoing webhooks</p>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 border-t border-gray-700 pt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveConfiguration}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                >
                  ðŸ’¾ Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ========================================
// RULE EDIT MODAL COMPONENT (COMPLETE)
// ========================================
function RuleEditModal({ rule, onSave, onCancel, onChange, gotifyEnabled }) {
  const [showWebhookConfig, setShowWebhookConfig] = useState(rule.webhook?.enabled || false);
  // Store signals as string for natural comma typing
  const [signalsText, setSignalsText] = useState(rule.requiredSignals?.join(', ') || '');

  // Update signals text when rule changes externally
  useEffect(() => {
    setSignalsText(rule.requiredSignals?.join(', ') || '');
  }, [rule.id]);

  const handleSignalsChange = (text) => {
    setSignalsText(text);
    // Only convert to array (don't filter) to preserve commas
    const signals = text.split(',').map(s => s.trim());
    onChange({ 
      ...rule, 
      requiredSignals: signals.filter(s => s) // Filter only for the rule object
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
      <div className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold">{rule.id ? 'Edit Rule' : 'Create Rule'}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-300 text-2xl">
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium mb-2">Rule Name</label>
            <input
              type="text"
              value={rule.name}
              onChange={(e) => onChange({ ...rule, name: e.target.value })}
              placeholder="My Trading Rule"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
            />
          </div>

          {/* Required Signals */}
          <div>
            <label className="block text-sm font-medium mb-2">Required Signals</label>
            <input
              type="text"
              value={signalsText}
              onChange={(e) => handleSignalsChange(e.target.value)}
              placeholder="RSI_OVERSOLD, MACD_CROSS, BREAKOUT"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
            />
            <small className="text-gray-400">Comma-separated list of signal types</small>
          </div>

          {/* Min Signals */}
          <div>
            <label className="block text-sm font-medium mb-2">Minimum Signals Required (X of N)</label>
            <input
              type="number"
              value={rule.minSignals || 2}
              onChange={(e) => onChange({ ...rule, minSignals: parseInt(e.target.value) || 1 })}
              min="1"
              max={rule.requiredSignals?.length || 10}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
            />
            <small className="text-gray-400">How many signals must match</small>
          </div>

          {/* Time Window */}
          <div>
            <label className="block text-sm font-medium mb-2">Time Window (seconds)</label>
            <input
              type="number"
              value={rule.timeWindow || 300}
              onChange={(e) => onChange({ ...rule, timeWindow: parseInt(e.target.value) || 300 })}
              min="10"
              max="3600"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
            />
            <small className="text-gray-400">Signals must occur within this time frame</small>
          </div>

          {/* Action */}
          <div>
            <label className="block text-sm font-medium mb-2">Action</label>
            <select
              value={rule.action || 'BUY'}
              onChange={(e) => onChange({ ...rule, action: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
            >
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
              <option value="long">LONG</option>
              <option value="short">SHORT</option>
              <option value="CLOSE">CLOSE</option>
            </select>
          </div>

          {/* Gotify Notification Toggle */}
          {gotifyEnabled && (
            <div className="border-t border-gray-700 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rule.notifyOnTrigger || false}
                  onChange={(e) => onChange({ ...rule, notifyOnTrigger: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="text-sm font-medium">🔔 Send Gotify notification when this rule triggers</span>
              </label>
            </div>
          )}

          {/* Webhook Configuration */}
          <div className="border-t border-gray-700 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showWebhookConfig}
                onChange={(e) => {
                  setShowWebhookConfig(e.target.checked);
                  onChange({
                    ...rule,
                    webhook: {
                      ...rule.webhook,
                      enabled: e.target.checked
                    }
                  });
                }}
                className="w-5 h-5"
              />
              <span className="text-sm font-medium">🚀 Enable Webhook</span>
            </label>
          </div>

          {showWebhookConfig && (
            <div className="space-y-4 pl-7 border-l-2 border-blue-600">
              <div>
                <label className="block text-sm font-medium mb-2">Webhook URL</label>
                <input
                  type="text"
                  value={rule.webhook?.url || ''}
                  onChange={(e) => onChange({
                    ...rule,
                    webhook: { ...rule.webhook, url: e.target.value }
                  })}
                  placeholder="https://example.com/webhook"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Method</label>
                <select
                  value={rule.webhook?.method || 'POST'}
                  onChange={(e) => onChange({
                    ...rule,
                    webhook: { ...rule.webhook, method: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Headers (JSON)</label>
                <textarea
                  value={JSON.stringify(rule.webhook?.headers || { 'Content-Type': 'application/json' }, null, 2)}
                  onChange={(e) => {
                    try {
                      const headers = JSON.parse(e.target.value);
                      onChange({
                        ...rule,
                        webhook: { ...rule.webhook, headers }
                      });
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg font-mono text-sm"
                  placeholder='{"Content-Type": "application/json"}'
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Payload (JSON with template variables)</label>
                <textarea
                  value={JSON.stringify(rule.webhook?.payload || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const payload = JSON.parse(e.target.value);
                      onChange({
                        ...rule,
                        webhook: { ...rule.webhook, payload }
                      });
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  rows={10}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg font-mono text-sm"
                  placeholder={`{
                      "symbol": "{{symbol}}",
                      "action": "{{action}}",
                      "rule": "{{rule}}",
                      "signals": "{{signals}}",
                      "signalCount": "{{signalCount}}",
                      "timestamp": "{{timestamp}}",
                      "open": "{{open}}",
                      "close": "{{close}}",
                      "high": "{{high}}",
                      "low": "{{low}}",
                      "volume": "{{volume}}"
                    }`}
                />
                <div className="mt-2 p-3 bg-blue-900/20 border border-blue-700 rounded text-xs">
                  <div className="font-semibold mb-2">🧪 Dynamic Template Variables</div>
                  <div className="space-y-1 text-gray-300">
                    <div><strong>Standard:</strong> {'{{'}<code>rule</code>{'}} {{'}<code>symbol</code>{'}} {{'}<code>action</code>{'}} {{'}<code>signals</code>{'}} {{'}<code>signalCount</code>{'}} {{'}<code>timestamp</code>{'}}'}</div>
                    <div><strong>OHLC:</strong> {'{{'}<code>open</code>{'}} {{'}<code>high</code>{'}} {{'}<code>low</code>{'}} {{'}<code>close</code>{'}} {{'}<code>volume</code>{'}}'}</div>
                    <div><strong>Custom:</strong> ANY field from incoming TradingView webhook can be used as {'{{'}<code>fieldname</code>{'}}'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-6 flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium">
            Cancel
          </button>
          <button onClick={onSave} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium">
            💾 Save Rule
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
